// ukraineRussia.js — Scenario 3: the Russo-Ukrainian war, Pokrovsk / Kurakhove axis
// (Oct 2024 – present), chosen because it's the most dynamic sector of the front through
// this period and shows sustained, multi-month positional change well on a timeline.
//
// Illustrative OSINT-style reconstruction for demoing MapEditorModal's multidate capability —
// approximate dates/positions drawn from widely-reported open coverage (ISW daily assessments,
// DeepState). NOT derived from classified or precise geolocated sourcing — see
// .scratch/map-editor-demo/PLAN.md.

import { buildScenarioDoc, makeLayer, makeTrack, dtg } from '../buildScenarioDoc';
import { sidc, AFFILIATION, SYMBOL_SET, TACTICAL_SIDC } from '../sidc';

const RU = sidc(AFFILIATION.HOSTILE, SYMBOL_SET.LAND_UNIT);
const RU_EQUIP = sidc(AFFILIATION.HOSTILE, SYMBOL_SET.LAND_EQUIPMENT);
const UA = sidc(AFFILIATION.FRIEND, SYMBOL_SET.LAND_UNIT);
const UA_EQUIP = sidc(AFFILIATION.FRIEND, SYMBOL_SET.LAND_EQUIPMENT);
const INSTALLATION_UA = sidc(AFFILIATION.FRIEND, SYMBOL_SET.LAND_INSTALLATION);

const T = {
    t0: dtg(2024, 10, 1),
    selydoveFalls: dtg(2024, 10, 29),
    kurakhoveEncircling: dtg(2024, 12, 1),
    kurakhoveFalls: dtg(2025, 1, 6),
    pokrovskPressure: dtg(2025, 2, 15),
    pokrovskFlanking: dtg(2025, 4, 20),
    myrnohradPressure: dtg(2025, 7, 1),
    t1: dtg(2025, 8, 15),
};

export const META = {
    id: 'ukraine-russia',
    title: 'Russo-Ukrainian War — Pokrovsk / Kurakhove Axis (Oct 2024 – Aug 2025)',
    summary: 'Ten months of the war\u2019s most active sector: Selydove and Kurakhove fall, then a slow grinding advance and flanking pressure on Pokrovsk/Myrnohrad.',
    dtgStartMs: T.t0,
    dtgEndMs: T.t1,
    initialDtgMs: T.pokrovskPressure,
    center: [37.15, 48.15],
    zoom: 9,
};

const layerRu = makeLayer({ label: 'Russian Forces' });
const layerUa = makeLayer({ label: 'Ukrainian Forces' });
const layerGraphics = makeLayer({ label: 'FLOT / Control Measures' });
const layerCities = makeLayer({ label: 'Contested Settlements' });

const tracks = [
    // -- Russian combined-arms army group grinding west along the Pokrovsk axis --------------
    makeTrack({
        layerId: layerRu.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.t0, { type: 'Point', coordinates: [37.55, 48.08] }, {
                sidc: RU, label: '2nd Combined Arms Army — approaching Selydove',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '2nd CAA', AS_COUNTRY: 'RUS' },
            }],
            [T.selydoveFalls, { type: 'Point', coordinates: [37.30, 48.14] }, {
                sidc: RU, label: '2nd Combined Arms Army — Selydove secured',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '2nd CAA', AS_COUNTRY: 'RUS' },
            }],
            [T.kurakhoveEncircling, { type: 'Point', coordinates: [37.20, 48.05] }, {
                sidc: RU, label: '2nd Combined Arms Army — encircling Kurakhove',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '2nd CAA', AS_COUNTRY: 'RUS' },
            }],
            [T.kurakhoveFalls, { type: 'Point', coordinates: [37.15, 47.98] }, {
                sidc: RU, label: '2nd Combined Arms Army — Kurakhove secured',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '2nd CAA', AS_COUNTRY: 'RUS' },
            }],
            [T.pokrovskPressure, { type: 'Point', coordinates: [37.05, 48.20] }, {
                sidc: RU, label: '2nd Combined Arms Army — pressing Pokrovsk from the south',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '2nd CAA', AS_COUNTRY: 'RUS' },
            }],
            [T.pokrovskFlanking, { type: 'Point', coordinates: [36.95, 48.25] }, {
                sidc: RU, label: '2nd Combined Arms Army — infiltration west of Pokrovsk',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '2nd CAA', AS_COUNTRY: 'RUS', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
            [T.myrnohradPressure, { type: 'Point', coordinates: [37.10, 48.28] }, {
                sidc: RU, label: '2nd Combined Arms Army — encircling Myrnohrad',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '2nd CAA', AS_COUNTRY: 'RUS' },
            }],
        ],
    }),
    // -- Russian armored spearhead (separate track, faster tempo) ---------------------------
    makeTrack({
        layerId: layerRu.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.kurakhoveEncircling, { type: 'Point', coordinates: [37.25, 48.02] }, {
                sidc: RU_EQUIP, label: '90th Tank Division — armored spearhead',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '90th Tank Div', AS_COUNTRY: 'RUS' },
            }],
            [T.pokrovskPressure, { type: 'Point', coordinates: [37.00, 48.18] }, {
                sidc: RU_EQUIP, label: '90th Tank Division — Pokrovsk southern approach',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '90th Tank Div', AS_COUNTRY: 'RUS' },
            }],
            [T.pokrovskFlanking, { type: 'Point', coordinates: [36.85, 48.27] }, {
                sidc: RU_EQUIP, label: '90th Tank Division — cutting the T-0511 highway',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '90th Tank Div', AS_COUNTRY: 'RUS' },
            }],
        ],
    }),
    // -- Ukrainian defending brigade — fighting withdrawal along the same axis --------------
    makeTrack({
        layerId: layerUa.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.t0, { type: 'Point', coordinates: [37.45, 48.10] }, {
                sidc: UA, label: '15th National Guard Bde — defending Selydove approach',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '15 NG Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'FULL' },
            }],
            [T.selydoveFalls, { type: 'Point', coordinates: [37.20, 48.18] }, {
                sidc: UA, label: '15th National Guard Bde — withdrawn to Kurakhove line',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '15 NG Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
            [T.kurakhoveFalls, { type: 'Point', coordinates: [36.95, 48.10] }, {
                sidc: UA, label: '15th National Guard Bde — reconstituting west of Kurakhove',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '15 NG Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
            [T.pokrovskPressure, { type: 'Point', coordinates: [36.95, 48.22] }, {
                sidc: UA, label: '15th National Guard Bde — Pokrovsk southern defense',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '15 NG Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'FULL' },
            }],
            [T.myrnohradPressure, { type: 'Point', coordinates: [37.05, 48.30] }, {
                sidc: UA, label: '15th National Guard Bde — Myrnohrad pocket defense',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '15 NG Bde', AS_COUNTRY: 'UKR', K_COMBAT_EFFECTIVENESS: 'MARGINAL' },
            }],
        ],
    }),
    // -- Ukrainian counter-battery/artillery group (stationary-ish, changes state over time) -
    makeTrack({
        layerId: layerUa.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.t0, { type: 'Point', coordinates: [37.02, 48.35] }, {
                sidc: UA_EQUIP, label: 'Artillery group — 2S22 Bohdana battery',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Bohdana battery', AS_COUNTRY: 'UKR' },
            }],
            [T.pokrovskPressure, { type: 'Point', coordinates: [36.85, 48.38] }, {
                sidc: UA_EQUIP, label: 'Artillery group — repositioned, out of Pokrovsk range fan',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Bohdana battery', AS_COUNTRY: 'UKR' },
            }],
        ],
    }),
    // -- Contested settlements: status pins, no movement (mobility=stationary) --------------
    makeTrack({
        layerId: layerCities.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.t0, { type: 'Point', coordinates: [37.30, 48.14] }, {
                sidc: INSTALLATION_UA, label: 'Selydove — Ukrainian-held',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Selydove', AS_COUNTRY: 'UKR' },
            }],
            [T.selydoveFalls, { type: 'Point', coordinates: [37.30, 48.14] }, {
                sidc: INSTALLATION_UA, label: 'Selydove — fallen',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Selydove', AS_COUNTRY: 'UKR', AL_OPERATIONAL_CONDITION: 'NMC' },
            }],
        ],
    }),
    makeTrack({
        layerId: layerCities.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.t0, { type: 'Point', coordinates: [37.15, 47.98] }, {
                sidc: INSTALLATION_UA, label: 'Kurakhove — Ukrainian-held',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Kurakhove', AS_COUNTRY: 'UKR' },
            }],
            [T.kurakhoveEncircling, { type: 'Point', coordinates: [37.15, 47.98] }, {
                sidc: INSTALLATION_UA, label: 'Kurakhove — encircled, contested',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Kurakhove', AS_COUNTRY: 'UKR', AL_OPERATIONAL_CONDITION: 'DAMAGED' },
            }],
            [T.kurakhoveFalls, { type: 'Point', coordinates: [37.15, 47.98] }, {
                sidc: INSTALLATION_UA, label: 'Kurakhove — fallen',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Kurakhove', AS_COUNTRY: 'UKR', AL_OPERATIONAL_CONDITION: 'NMC' },
            }],
        ],
    }),
    makeTrack({
        layerId: layerCities.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.t0, { type: 'Point', coordinates: [37.17, 48.29] }, {
                sidc: INSTALLATION_UA, label: 'Pokrovsk — Ukrainian-held, key logistics hub',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Pokrovsk', AS_COUNTRY: 'UKR' },
            }],
            [T.pokrovskFlanking, { type: 'Point', coordinates: [37.17, 48.29] }, {
                sidc: INSTALLATION_UA, label: 'Pokrovsk — flanked north and south, resupply contested',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Pokrovsk', AS_COUNTRY: 'UKR', AL_OPERATIONAL_CONDITION: 'DAMAGED' },
            }],
        ],
    }),
    // -- FLOT tracing the front's westward creep across the whole window --------------------
    makeTrack({
        layerId: layerGraphics.layerId,
        elementType: 'tactical-line',
        mobility: null,
        graphicType: 'feba',
        snapshots: [
            [T.t0, {
                type: 'LineString',
                coordinates: [[37.60, 47.90], [37.45, 48.10], [37.35, 48.30], [37.30, 48.50]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — 1 Oct 2024' }],
            [T.selydoveFalls, {
                type: 'LineString',
                coordinates: [[37.35, 47.90], [37.20, 48.14], [37.15, 48.32], [37.20, 48.50]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — Selydove falls' }],
            [T.kurakhoveFalls, {
                type: 'LineString',
                coordinates: [[37.10, 47.85], [37.00, 48.05], [37.00, 48.28], [37.10, 48.50]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — Kurakhove falls' }],
            [T.pokrovskFlanking, {
                type: 'LineString',
                coordinates: [[37.00, 47.85], [36.90, 48.10], [36.85, 48.30], [37.00, 48.50]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — Pokrovsk flanked' }],
            [T.t1, {
                type: 'LineString',
                coordinates: [[36.95, 47.85], [36.85, 48.12], [36.90, 48.33], [37.05, 48.50]],
            }, { sidc: TACTICAL_SIDC.FLOT, label: 'FLOT — 15 Aug 2025' }],
        ],
    }),
    // -- Russian main-effort axis-of-advance arrow, updated at each phase --------------------
    makeTrack({
        layerId: layerGraphics.layerId,
        elementType: 'tactical-line',
        mobility: null,
        graphicType: 'axis-of-advance',
        snapshots: [
            [T.t0, {
                type: 'LineString',
                coordinates: [[37.55, 48.08], [37.30, 48.14]],
            }, { sidc: TACTICAL_SIDC.DIRATK_GROUND, label: 'Main effort — Selydove' }],
            [T.kurakhoveEncircling, {
                type: 'LineString',
                coordinates: [[37.30, 48.05], [37.15, 47.98]],
            }, { sidc: TACTICAL_SIDC.DIRATK_GROUND, label: 'Main effort — Kurakhove' }],
            [T.pokrovskPressure, {
                type: 'LineString',
                coordinates: [[37.15, 47.98], [37.05, 48.20]],
            }, { sidc: TACTICAL_SIDC.DIRATK_GROUND, label: 'Main effort — Pokrovsk (south)' }],
            [T.pokrovskFlanking, {
                type: 'LineString',
                coordinates: [[37.10, 48.10], [36.90, 48.27]],
            }, { sidc: TACTICAL_SIDC.DIRATK_GROUND, label: 'Supporting effort — Pokrovsk (west flank)' }],
        ],
    }),
];

export const doc = buildScenarioDoc({
    dtgStartMs: META.dtgStartMs,
    dtgEndMs: META.dtgEndMs,
    layers: [layerRu, layerUa, layerGraphics, layerCities],
    tracks,
    views: [{ name: 'Pokrovsk axis', bbox: [[36.7, 47.8], [37.7, 48.6]] }],
});
